import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { Button } from "../button"
import { Icon } from "../icon"
import { VisuallyHidden } from "../visually-hidden"
import { useNoteContext } from "./note-context"
import type { FocusTarget } from "./note-focus"

export type NoteDismissProps = {
  /** Called when the note is dismissed, before it unmounts. Use it to persist the dismissal or track it. */
  onDismiss?: () => void
  /** Element to focus after dismissal, as a ref or a function that returns the element. Defaults to the next focusable element after the note, or the previous one when nothing follows. */
  focusTarget?: FocusTarget
  /** Replaces the default close button, e.g. with a `Button` placed inside `Note.Actions`. */
  render?: React.ReactElement
}

/**
 * A button that dismisses the note. Renders a close button in the note's
 * dismiss slot by default, or the `render` element anywhere in the note.
 */
export function NoteDismiss({
  onDismiss,
  focusTarget,
  render
}: NoteDismissProps) {
  const { dismiss } = useNoteContext()

  const trigger = useRender({
    render: render ?? (
      <Button
        className="relative after:content-[''] after:absolute after:-inset-1"
        variant="tertiary"
        size="small"
      >
        <Icon name="close" />
        <VisuallyHidden>Dismiss this message</VisuallyHidden>
      </Button>
    ),
    props: {
      onClick: () => {
        onDismiss?.()
        dismiss(focusTarget)
      }
    }
  })

  if (render) return trigger

  return (
    <div
      className="[grid-area:dismiss] size-6 flex items-center justify-center"
      data-slot="note-dismiss"
    >
      {trigger}
    </div>
  )
}
